// src/components/OrderInfoModal.js
import React, { useState, useEffect } from "react";
import "./OrderInfoModal.css";
import { deleteWorkTime, editOn } from "../assets"; // ⟵ poprawny import
import axios from "axios";
import WorkTimeModal from "./WorkTimeModal";
import MaterialModal from "./MaterialModal";
import { API_URL } from "../config";

const TIME_OPTIONS = Array.from({ length: 25 }, (_, h) =>
  `${String(h).padStart(2, "0")}:00`
);

// === [NOWE – pomocnicze do cen] ===
function round2(n) {
  const x = Number(n);
  return Math.round((x + Number.EPSILON) * 100) / 100;
}
const renderMaterialUnitLabel = (qtyType) =>
  qtyType === "metry" ? "Cena za metr" : "Cena za sztukę";
// ================================

function calcHoursForEdit(start, end) {
  if (!start || !end) return "";
  if (start === "00:00" && end === "00:00") return 24;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  const s = sh * 60 + sm;
  const e = eh * 60 + em;
  const diff = (e - s) / 60;
  if (diff <= 0) return "";
  return diff;
}

// Format H:MM do wyświetlania (np. "07:00" -> "7:00")
function fmtTime(t) {
  if (!t || typeof t !== "string") return "";
  const [h, m] = t.split(":");
  const hh = String(parseInt(h, 10)); // "00" -> "0", "07" -> "7", "15" -> "15"
  return `${hh}:${m}`;
}

/**
 * NOWE: Normalizacja czasu z bazy/rekordu do pełnej godziny "HH:00"
 * Obsługuje "13:00", "13:00:00", "13", "07:30" (zetnie do "07:00"), "24:00", "24:00:00".
 */
function normalizeToFullHour(value) {
  if (value === null || value === undefined) return "";
  let s = String(value).trim();
  if (!s) return "";
  // Utnij sekundy jeśli są (HH:MM:SS -> HH:MM)
  const parts = s.split(":");
  let h = parts[0];
  let m = parts[1] ?? "00";

  // Zamiana na liczby + sanity check
  let hh = parseInt(h, 10);
  if (Number.isNaN(hh)) return "";

  // Jeśli godzina 24 -> zawsze "24:00"
  if (hh === 24) return "24:00";

  // Zakres 0..23
  if (hh < 0) hh = 0;
  if (hh > 24) hh = 24;

  // Zawsze pełna godzina :00 (TIME_OPTIONS ma tylko pełne)
  return `${String(hh).padStart(2, "0")}:00`;
}

function OrderInfoModal({ orderId, onClose, user, initialTab = "time" }) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [workTimeData, setWorkTimeData] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editMaterial, setEditMaterial] = useState(null);
  const [deleteMaterialId, setDeleteMaterialId] = useState(null);
  const [password, setPassword] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showWorkTimeModal, setShowWorkTimeModal] = useState(false);
  const [showMaterialModal, setShowMaterialModal] = useState(false);

  // kontrola rozwiniętych szczegółów wpisów czasu pracy (per ID)
  const [expandedEntries, setExpandedEntries] = useState({});

  // modal edycji czasu pracy
  const [editTimeEntry, setEditTimeEntry] = useState(null);
  const [editWorkDate, setEditWorkDate] = useState("");
  const [editStart, setEditStart] = useState("07:00");
  const [editEnd, setEditEnd] = useState("15:00");
  const [editError, setEditError] = useState("");

  useEffect(() => {
    setActiveTab(initialTab || "time");
  }, [initialTab]);

  useEffect(() => {
    if (activeTab === "time") {
      fetchWorkTimeData();
    } else if (activeTab === "material") {
      fetchMaterials();
    }
  }, [activeTab, orderId]);

  // NOWE: ESC zamyka modal edycji czasu pracy
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape" && editTimeEntry) {
        setEditTimeEntry(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [editTimeEntry]);

  const fetchWorkTimeData = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/orders/${orderId}/worktime`,
        { credentials: "include" }
      );
      if (!response.ok)
        throw new Error("Błąd pobierania danych o czasie pracy.");
      const data = await response.json();
      setWorkTimeData(data);
      setError("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchMaterials = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/materials/${orderId}`,
        { credentials: "include" }
      );
      if (!response.ok) throw new Error("Błąd pobierania materiałów.");
      const data = await response.json();
      setMaterials(data);
      setError("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const hasPermission = (material) => {
    if (!user) return false;
    return (
      user.role === "Admin" ||
      user.role === "Moderator" ||
      user.position === "Logistyk" ||
      material.user_id === user.id
    );
  };

  const handleEdit = (material) => {
    setEditMaterial(material);
  };
  const handleDelete = (id) => setDeleteMaterialId(id);

  const deleteWorkTimeEntry = async (entryId) => {
    if (!window.confirm("Usunąć ten wpis czasu pracy?")) return;
    try {
      await axios.delete(
        `${API_URL}/orders/working_time/${entryId}`,
        { withCredentials: true }
      );
      fetchWorkTimeData();
    } catch (err) {
      alert("Błąd usuwania czasu pracy");
      console.error(err);
    }
  };

  const openEditTimeModal = (entry) => {
    setEditTimeEntry(entry);
    const d = new Date(entry.work_date);
    const yyyyMMdd = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
      .toISOString()
      .split("T")[0];
    setEditWorkDate(yyyyMMdd);

    // ⬇️ NOWE: normalizacja, żeby selekty miały dokładnie opcje z TIME_OPTIONS
    const start = normalizeToFullHour(entry.start_time);
    const end = normalizeToFullHour(entry.end_time);
    setEditStart(start || "07:00");
    setEditEnd(end || "15:00");

    setEditError("");
  };

  const saveEditTime = async () => {
    if (!editWorkDate) {
      setEditError("Wybierz datę.");
      return;
    }
    if (!TIME_OPTIONS.includes(editStart) || !TIME_OPTIONS.includes(editEnd)) {
      setEditError("Godziny muszą być pełne, od 00:00 do 24:00.");
      return;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const chosen = new Date(editWorkDate);
    chosen.setHours(0, 0, 0, 0);
    if (chosen > today) {
      setEditError("Data nie może być w przyszłości.");
      return;
    }

    const hours = calcHoursForEdit(editStart, editEnd);
    if (hours === "" || hours <= 0 || hours > 24) {
      setEditError(
        "Zakres godzin jest nieprawidłowy (zakres przez północ edytuj jako dwa wpisy)."
      );
      return;
    }

    try {
      await axios.put(
        `${API_URL}/orders/working_time/${editTimeEntry.id}`,
        {
          work_date: editWorkDate,
          start_time: editStart,
          end_time: editEnd,
        },
        { withCredentials: true }
      );
      setEditTimeEntry(null);
      await fetchWorkTimeData();
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.error || "Błąd podczas zapisywania zmian.";
      setEditError(msg);
    }
  };

  // === [ZMIANA – zapis materiału z ceną jednostkową i łączną] ===
  const saveChanges = async () => {
    if (
      !editMaterial.category ||
      !editMaterial.name ||
      !editMaterial.quantity_type ||
      editMaterial.quantity === undefined ||
      editMaterial.price_per_piece === undefined ||
      editMaterial.price_per_piece === "" ||
      !editMaterial.currency
    ) {
      alert("Wypełnij wszystkie wymagane pola (w tym cenę za sztukę/metr)");
      return;
    }

    const qty = Number(editMaterial.quantity);
    const ppp = Number(editMaterial.price_per_piece);
    if (Number.isNaN(qty) || qty <= 0) {
      alert("Nieprawidłowa ilość.");
      return;
    }
    if (Number.isNaN(ppp) || ppp < 0) {
      alert("Nieprawidłowa cena jednostkowa.");
      return;
    }

    const computedTotal = round2(qty * ppp);

    try {
      await axios.put(
        `${API_URL}/materials/${editMaterial.id}`,
        {
          ...editMaterial,
          quantity: qty,
          price_per_piece: round2(ppp),
          price: computedTotal, // łączna (do kolumny price)
        },
        { withCredentials: true }
      );
      setSaveSuccess(true);
      setTimeout(() => {
        setEditMaterial(null);
        setSaveSuccess(false);
        fetchMaterials();
      }, 1500);
    } catch (error) {
      alert("Błąd podczas zapisywania zmian");
      console.error(error);
    }
  };
  // ===============================================================

  const confirmDelete = async () => {
    try {
      await axios.delete(
        `${API_URL}/materials/${deleteMaterialId}`,
        {
          withCredentials: true,
          data: { password },
        }
      );
      setDeleteMaterialId(null);
      setPassword("");
      fetchMaterials();
    } catch (error) {
      if (error.response && error.response.status === 401) {
        setDeleteError("Nieprawidłowe hasło");
      } else {
        setDeleteError("Błąd podczas usuwania");
      }
      console.error(error);
    }
  };

  // przełączanie szczegółów dla danego wpisu
  const toggleDetails = (entryId) => {
    setExpandedEntries((prev) => ({
      ...prev,
      [entryId]: !prev[entryId],
    }));
  };

  const renderTimeTab = () => {
    if (loading) return <p>Ładowanie...</p>;
    if (error) return <p style={{ color: "red" }}>{error}</p>;
    if (!workTimeData || workTimeData.entries.length === 0)
      return <p>Brak danych o czasie pracy.</p>;

    const { totalHours, summary, entries } = workTimeData;

    return (
      <div className="worktime-tab">
        {/* Karta z podsumowaniem */}
        <div className="summary-card">
          <div className="summary-row">
            <span className="summary-label">Łącznie godzin:</span>
            <span className="summary-badge">{totalHours} h</span>
          </div>

          <h4 className="summary-title">Podsumowanie czasu pracy</h4>
          <div className="summary-grid">
            {Object.entries(summary).map(([name, hours]) => (
              <div key={name} className="summary-item">
                <span className="summary-name">{name}</span>
                <span className="summary-hours">{hours} h</span>
              </div>
            ))}
          </div>
        </div>

        {/* Lista wpisów jako „kafle” */}
        <h4 className="entries-title">Szczegółowe wpisy</h4>
        <div className="worktime-list">
          {entries.map((entry) => {
            const isOpen = !!expandedEntries[entry.id];
            const detailsLine = `wpisane przez: ${entry.adder_first_name} ${entry.adder_last_name} dnia: ${new Date(
              entry.created_at
            ).toLocaleString("pl-PL")}`;

            const canEdit =
              user &&
              (user.role === "Admin" ||
                Number(entry.added_by) === Number(user.id) ||
                Number(entry.user_id) === Number(user.id));

            const canDelete =
              user &&
              (user.role === "Admin" ||
                Number(entry.added_by) === Number(user.id) ||
                Number(entry.user_id) === Number(user.id));

            return (
              <div key={entry.id} className="worktime-card">
                <div className="worktime-card-header">
                  <div className="worktime-main">
                    <span className="worktime-name">
                      {entry.worker_first_name} {entry.worker_last_name}
                    </span>
                    <span className="worktime-dot">•</span>
                    <span className="worktime-hours">{entry.hours} h</span>
                    <span className="worktime-dot">•</span>
                    <span className="worktime-date">
                      {new Date(entry.work_date).toLocaleDateString("pl-PL")}
                    </span>

                    {/* NOWE: zakres godzin – zawsze widoczny */}
                    {entry.start_time && entry.end_time && (
                      <>
                        <span className="worktime-dot">•</span>
                        <span className="worktime-window">
                          od {fmtTime(entry.start_time)} do{" "}
                          {fmtTime(entry.end_time)}
                        </span>
                      </>
                    )}
                  </div>

                  <div className="worktime-actions">
                    <button
                      className="details-toggle-btn"
                      onClick={() => toggleDetails(entry.id)}
                    >
                      {isOpen ? "Ukryj szczegóły" : "Pokaż szczegóły"}
                    </button>

                    {canEdit && (
                      <img
                        src={editOn}
                        alt="Edytuj"
                        className="edit-worktime-icon"
                        title="Edytuj wpis"
                        onClick={() => openEditTimeModal(entry)}
                      />
                    )}

                    {canDelete && (
                      <img
                        src={deleteWorkTime}
                        alt="Usuń"
                        className="delete-worktime-icon"
                        title="Usuń wpis"
                        onClick={() => deleteWorkTimeEntry(entry.id)}
                      />
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div className="worktime-details" aria-live="polite">
                    <span className="details-label">Wpis:</span>
                    <span className="details-value">{detailsLine}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderMaterialTab = () => {
    if (loading) return <p>Ładowanie...</p>;
    if (error) return <p style={{ color: "red" }}>{error}</p>;
    if (materials.length === 0) return <p>Brak dodanych materiałów.</p>;

    return (
		<div className="materials-container">
		  {materials.map((material, index) => (
			<div key={index} className="material-card">
			  <h4>{material.name}</h4>

			  {/* ✅ NOWY UKŁAD 2-KOLUMNOWY */}
			  <div className="material-details-grid">
				{/* LEWA KOLUMNA */}
				<div className="material-col material-col-left">
				  <p>
					<strong>Nr ref:</strong> {material.nr_ref}
				  </p>

				  <p>
					<strong>Kategoria:</strong> {material.category}
				  </p>

				  {material.manufacturer && (
					<p>
					  <strong>Producent:</strong> {material.manufacturer}
					</p>
				  )}

				  {material.symbol && (
					<p>
					  <strong>Symbol:</strong> {material.symbol}
					</p>
				  )}

				  {material.catalog_number && (
					<p>
					  <strong>Numer katalogowy:</strong> {material.catalog_number}
					</p>
				  )}

				  {material.serial_number && (
					<p>
					  <strong>Numer seryjny:</strong> {material.serial_number}
					</p>
				  )}

				  {material.mac_address && (
					<p>
					  <strong>Adres MAC:</strong> {material.mac_address}
					</p>
				  )}

				  {material.designation && (
					<p>
					  <strong>Oznaczenie:</strong> {material.designation}
					</p>
				  )}

				  <p>
					<strong>Pobrał:</strong> {material.first_name} {material.last_name}
				  </p>

				  <p>
					<strong>Data:</strong>{" "}
					{new Date(material.created_at).toLocaleString("pl-PL")}
				  </p>

				  {material.additional_description && (
					<p>
					  <strong>Dodatkowy opis:</strong> {material.additional_description}
					</p>
				  )}
				</div>

				{/* PRAWA KOLUMNA */}
				<div className="material-col material-col-right">
				  <p className="material-metric">
					<strong>Ilość:</strong> {material.quantity}{" "}
					{material.quantity_type === "metry" ? "m" : "szt."}
				  </p>

				  {"price_per_piece" in material && (
					<p className="material-metric">
					  <strong>{renderMaterialUnitLabel(material.quantity_type)}:</strong>{" "}
					  {material.price_per_piece} {material.currency}
					</p>
				  )}

				  <p className="material-metric material-total">
					<strong>Łącznie:</strong> {material.price} {material.currency}
				  </p>
				</div>
			  </div>

			  {hasPermission(material) && (
				<div className="material-actions">
				  <button className="edit-btn" onClick={() => handleEdit(material)}>
					Edytuj
				  </button>
				  <button className="delete-btn" onClick={() => handleDelete(material.id)}>
					Usuń
				  </button>
				</div>
			  )}
			</div>
		  ))}
		</div>
    );
  };

  const hasNestedOpen =
    showWorkTimeModal ||
    showMaterialModal ||
    !!editMaterial ||
    !!deleteMaterialId ||
    !!editTimeEntry;

  return (
    <div
      className="modal-overlay"
      onClick={() => {
        if (!hasNestedOpen) onClose();
      }}
    >
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Informacje o zleceniu</h3>

          <div className="modal-header-actions">
            <button
              type="button"
              className="modal-close-btn"
              onClick={onClose}
              aria-label="Zamknij modal"
              title="Zamknij"
            >
              ×
            </button>
          </div>
        </div>

        <div className="modal-tabs">
          <button
            className={`tab-button ${activeTab === "time" ? "active" : ""}`}
            onClick={() => setActiveTab("time")}
          >
            Czas Pracy
          </button>
          <button
            className={`tab-button ${activeTab === "material" ? "active" : ""}`}
            onClick={() => setActiveTab("material")}
          >
            Materiał
          </button>
        </div>

        <div className="tab-actions">
          {activeTab === "time" ? (
            <button
              className="tab-action-btn"
              onClick={() => setShowWorkTimeModal(true)}
            >
              Dodaj Czas Pracy
            </button>
          ) : (
            <button
              className="tab-action-btn"
              onClick={() => setShowMaterialModal(true)}
            >
              Dodaj Materiał
            </button>
          )}
        </div>

        <div className="modal-body">
          {activeTab === "time" ? renderTimeTab() : renderMaterialTab()}
        </div>
      </div>

      {/* Modal edycji MATERIAŁU */}
      {editMaterial && (
        <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
          <h3>Edytuj materiał</h3>
          <select
            value={editMaterial.category}
            onChange={(e) =>
              setEditMaterial({ ...editMaterial, category: e.target.value })
            }
          >
            <option value="">Wybierz kategorię</option>
            {[
              "Kable Elektryczne",
              "Przewody Sygnałowe",
              "Urządzenia",
              "Elementy Automatyki",
              "Elementy Elektryczne",
              "Elementy Ogólne",
              "IT/Sieci",
              "BHP",
              "Biurowe",
              "Inne",
            ].map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Nazwa"
            value={editMaterial.name || ""}
            onChange={(e) =>
              setEditMaterial({ ...editMaterial, name: e.target.value })
            }
          />
          <input
            type="text"
            placeholder="Producent"
            value={editMaterial.manufacturer || ""}
            onChange={(e) =>
              setEditMaterial({ ...editMaterial, manufacturer: e.target.value })
            }
          />
          <input
            type="text"
            placeholder="Symbol"
            value={editMaterial.symbol || ""}
            onChange={(e) =>
              setEditMaterial({ ...editMaterial, symbol: e.target.value })
            }
          />
          <input
            type="text"
            placeholder="Dodatkowy opis"
            value={editMaterial.additional_description || ""}
            onChange={(e) =>
              setEditMaterial({
                ...editMaterial,
                additional_description: e.target.value,
              })
            }
          />
          <input
            type="text"
            placeholder="Numer katalogowy"
            value={editMaterial.catalog_number || ""}
            onChange={(e) =>
              setEditMaterial({
                ...editMaterial,
                catalog_number: e.target.value,
              })
            }
          />
          <input
            type="text"
            placeholder="Numer seryjny"
            value={editMaterial.serial_number || ""}
            onChange={(e) =>
              setEditMaterial({
                ...editMaterial,
                serial_number: e.target.value,
              })
            }
          />
          <input
            type="text"
            placeholder="Adres MAC"
            value={editMaterial.mac_address || ""}
            onChange={(e) =>
              setEditMaterial({ ...editMaterial, mac_address: e.target.value })
            }
          />
          <input
            type="text"
            placeholder="Oznaczenie"
            value={editMaterial.designation || ""}
            onChange={(e) =>
              setEditMaterial({ ...editMaterial, designation: e.target.value })
            }
          />
          <select
            value={editMaterial.quantity_type}
            onChange={(e) =>
              setEditMaterial({
                ...editMaterial,
                quantity_type: e.target.value,
              })
            }
          >
            <option value="">Wybierz typ ilości</option>
            <option value="metry">metry</option>
            <option value="sztuki">sztuki</option>
          </select>
          <input
            type="number"
            placeholder="Ilość"
            value={editMaterial.quantity || ""}
            onChange={(e) =>
              setEditMaterial({ ...editMaterial, quantity: e.target.value })
            }
          />

          {/* === [ZMIANA] – cena jednostkowa zamiast 'Cena' + podgląd łącznej === */}
          <input
            type="number"
            step="0.01"
            placeholder={renderMaterialUnitLabel(editMaterial.quantity_type)}
            value={
              editMaterial.price_per_piece !== undefined
                ? editMaterial.price_per_piece
                : ""
            }
            onChange={(e) =>
              setEditMaterial({
                ...editMaterial,
                price_per_piece: e.target.value,
              })
            }
          />
          <div style={{ margin: "6px 0", fontSize: 14, opacity: 0.85 }}>
            Łącznie:&nbsp;
            {(() => {
              const qty = Number(editMaterial.quantity);
              const ppp = Number(editMaterial.price_per_piece);
              if (Number.isNaN(qty) || Number.isNaN(ppp)) return "—";
              return `${round2(qty * ppp)} ${editMaterial.currency || ""}`;
            })()}
          </div>
          {/* ================================================================ */}

          <select
            value={editMaterial.currency}
            onChange={(e) =>
              setEditMaterial({ ...editMaterial, currency: e.target.value })
            }
          >
            <option value="">Wybierz walutę</option>
            <option value="zł">zł</option>
            <option value="$">$</option>
            <option value="€">€</option>
          </select>
          <div>
            <button className="edit-btn" onClick={saveChanges}>
              Zapisz Zmiany
            </button>
            <button className="cancel-btn" onClick={() => setEditMaterial(null)}>
              Anuluj
            </button>
          </div>
          {saveSuccess && <p className="success-message">Zapisano</p>}
        </div>
      )}

      {/* Modal usuwania MATERIAŁU */}
      {deleteMaterialId && (
        <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
          <h3>Potwierdź usunięcie</h3>
          <input
            type="password"
            placeholder="Wprowadź hasło"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {deleteError && <p className="error-message">{deleteError}</p>}
          <div>
            <button className="delete-btn" onClick={confirmDelete}>
              Usuń
            </button>
            <button
              className="cancel-btn"
              onClick={() => setDeleteMaterialId(null)}
            >
              Anuluj
            </button>
          </div>
        </div>
      )}

      {/* Modal dodawania CZASU PRACY */}
      {showWorkTimeModal && (
        <WorkTimeModal
          orderId={orderId}
          onClose={() => setShowWorkTimeModal(false)}
          onSuccess={() => {
            setShowWorkTimeModal(false);
            if (activeTab === "time") fetchWorkTimeData();
          }}
        />
      )}

      {/* Modal EDYCJI CZASU PRACY – nowy wygląd */}
      {editTimeEntry && (
        <div className="wt-edit-overlay" onClick={(e) => e.stopPropagation()}>
          <div
            className="wt-edit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="wt-edit-title"
          >
            <div className="wt-edit-header">
              <h4 id="wt-edit-title" className="wt-edit-title">Edytuj czas pracy</h4>
              <button
                type="button"
                className="wt-edit-close"
                aria-label="Zamknij"
                title="Zamknij"
                onClick={() => setEditTimeEntry(null)}
              >
                ×
              </button>
            </div>

            <div className="wt-edit-content">
              <div className="wt-edit-meta">
                <div className="wt-avatar">
                  {`${(editTimeEntry.worker_first_name || "")[0] || ""}${
                    (editTimeEntry.worker_last_name || "")[0] || ""
                  }`.toUpperCase()}
                </div>
                <div className="wt-meta-text">
                  <div className="wt-person">
                    {editTimeEntry.worker_first_name} {editTimeEntry.worker_last_name}
                  </div>
                  <label className="wt-date-label">
                    Data pracy:
                    <input
                      type="date"
                      value={editWorkDate}
                      max={new Date().toISOString().split("T")[0]}
                      onChange={(e) => setEditWorkDate(e.target.value)}
                    />
                  </label>
                </div>
              </div>

              <div className="wt-time-grid">
                <div className="wt-time-col">
                  <label>Początek</label>
                  <select
                    value={editStart}
                    onChange={(e) => setEditStart(e.target.value)}
                  >
                    {TIME_OPTIONS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="wt-time-arrow" aria-hidden>→</div>

                <div className="wt-time-col">
                  <label>Koniec</label>
                  <select
                    value={editEnd}
                    onChange={(e) => setEditEnd(e.target.value)}
                  >
                    {TIME_OPTIONS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="wt-hours-row">
                <span className="wt-hours-label">Wyliczone godziny</span>
                <span className="wt-hours-chip">
                  {calcHoursForEdit(editStart, editEnd) || "—"} h
                </span>
              </div>

              {editError && <p className="error-message">{editError}</p>}

              <div className="wt-edit-actions">
                <button className="cancel-btn" onClick={() => setEditTimeEntry(null)}>
                  Anuluj
                </button>
                <button className="edit-btn" onClick={saveEditTime}>
                  Zapisz
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal dodawania MATERIAŁU */}
      {showMaterialModal && (
        <MaterialModal
          orderId={orderId}
          onClose={() => setShowMaterialModal(false)}
          onSave={() => {
            setShowMaterialModal(false);
            if (activeTab === "material") fetchMaterials();
          }}
        />
      )}
    </div>
  );
}

export default OrderInfoModal;
